/**
 * ARTICLE D'EXEMPLE — généré le 2026-09-06 pour valider la maquette du blog.
 * À relire, compléter ou remplacer par du contenu réel avant/après mise en ligne.
 * Sources : 6 URL vérifiées automatiquement (4 OK), article révisé après vérification.
 * Format : voir src/blog/README.md
 */
export default {
  "slug": "temis-technopole-startups-deeptech-besancon",
  "title": "TEMIS, la technopole où naissent les startups deeptech de Besançon",
  "excerpt": "Au nord-ouest de Besançon, TEMIS réunit laboratoires, écoles et entreprises des microtechniques. Tour d’horizon des dispositifs qui accompagnent les startups deeptech, du labo à la pépinière.",
  "seoTitle": "TEMIS, la technopole des startups deeptech",
  "seoDescription": "La technopole TEMIS réunit FEMTO-ST, écoles d’ingénieurs et entreprises des microtechniques : le parcours des startups deeptech, du laboratoire à la pépinière.",
  "category": "startups",
  "tags": [
    "TEMIS",
    "Deeptech",
    "FEMTO-ST",
    "Incubation",
    "Microtechniques"
  ],
  "author": "jimmy",
  "publishedAt": "2026-08-28",
  "updatedAt": "2026-08-28",
  "featured": false,
  "cover": {
    "image": "/img/blog/20100220-temis-100-4373781200.jpg",
    "credit": "Photo : TEMIS, Besançon — Régions Démocrates 2010, CC BY 2.0, via Wikimedia Commons",
    "alt": "Bâtiments modernes aux façades vitrées de la technopole TEMIS à Besançon, sous un ciel clair, avec des bâtiments de laboratoire en arrière-plan"
  },
  "content": [
    {
      "type": "lead",
      "text": "Au nord-ouest de Besançon, la technopole TEMIS réunit sur un même plateau un grand laboratoire, des écoles d’ingénieurs et des dizaines d’entreprises spécialisées dans les microtechniques. C’est là que naît une bonne part des startups deeptech bisontines. Décryptage d’un parcours qui mène du laboratoire à la pépinière, et des dispositifs qui l’accompagnent."
    },
    {
      "type": "keyfacts",
      "title": "En bref",
      "facts": [
        {
          "label": "Nom complet",
          "value": "Technopole Microtechnique et Scientifique"
        },
        {
          "label": "Sites",
          "value": "Site principal au nord-ouest de Besançon, près du campus scientifique ; TEMIS Santé à proximité du CHU"
        },
        {
          "label": "Acteurs académiques",
          "value": "FEMTO-ST, SUPMICROTECH-ENSMM, Université Marie et Louis Pasteur, ISIFC"
        },
        {
          "label": "Bâtiment phare",
          "value": "TEMIS Innovation – Maison des Microtechniques (pépinière et salle blanche)"
        },
        {
          "label": "Domaines",
          "value": "Microtechniques, micro-nanosystèmes, temps-fréquence, optique, dispositifs médicaux"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Une technopole héritière de l’horlogerie"
    },
    {
      "type": "p",
      "text": "Besançon a longtemps été la capitale française de l’horlogerie. Quand cette industrie a décliné, ses savoir-faire de précision se sont reconvertis dans les microtechniques : petites pièces usinées, capteurs, microsystèmes. [TEMIS](https://www.temis.org/), pour Technopole Microtechnique et Scientifique, est la traduction urbaine de cette reconversion. Aménagée autour du campus scientifique, elle regroupe laboratoires, formations et entreprises.[^1] Grand Besançon Métropole porte la technopole et en assure l’animation économique.[^5]"
    },
    {
      "type": "p",
      "text": "Le pivot académique du site est l’institut [FEMTO-ST](https://www.femto-st.fr/), né en 2004 de la fusion de plusieurs laboratoires franc-comtois. Unité mixte du CNRS, de l’université, de SUPMICROTECH-ENSMM et de l’UTBM, il compte parmi les grands laboratoires français de sciences pour l’ingénieur, avec des départements allant de l’optique et du temps-fréquence aux micro-nanosystèmes, à la microrobotique et à l’informatique.[^2] À quelques pas se trouvent SUPMICROTECH-ENSMM, école d’ingénieurs tournée vers la mécanique et les microtechniques,[^4] [l’ISIFC](/blog/isifc-nouveau-batiment-inaugure-2025), école d’ingénieurs biomédicaux de l’université, et les composantes scientifiques de l’Université de Franche-Comté, devenue depuis 2025 l’Université Marie et Louis Pasteur, dont l’école et l’UTBM sont désormais des établissements-composantes.[^3]"
    },
    {
      "type": "h2",
      "text": "Du laboratoire à l’entreprise : un parcours en étapes"
    },
    {
      "type": "p",
      "text": "Une startup deeptech part d’un résultat de recherche qu’il faut protéger, prouver, puis industrialiser. À Besançon, ce parcours suit une chaîne assez lisible, dont chaque maillon se trouve sur TEMIS ou à proximité immédiate."
    },
    {
      "type": "ol",
      "items": [
        "**La maturation.** Un brevet ou un prototype de laboratoire est consolidé, souvent avec l’appui de la SATT Sayens, société de transfert de technologies qui couvre la Bourgogne-Franche-Comté et une partie du Grand Est : propriété intellectuelle, financement de preuves de concept, recherche de licenciés.",
        "**L’incubation.** L’incubateur régional DECA-BFC accompagne les porteurs de projets issus de la recherche publique : étude de marché, modèle économique, premiers financements, constitution de l’équipe.",
        "**La pépinière.** Une fois la société créée, elle peut s’installer à TEMIS Innovation – Maison des Microtechniques, qui propose bureaux et ateliers à des conditions adaptées aux jeunes entreprises, des services partagés et la proximité immédiate de la centrale de technologie MIMENTO de FEMTO-ST, une salle blanche accessible aux entreprises dans le cadre de collaborations.[^1]",
        "**L’implantation.** Après quelques années, l’entreprise rejoint un hôtel d’entreprises ou construit ses propres locaux sur la technopole, sans quitter l’écosystème."
      ]
    },
    {
      "type": "p",
      "text": "Plusieurs sociétés issues de FEMTO-ST ou des laboratoires qui l’ont précédé ont suivi ce chemin et restent installées sur la technopole, par exemple dans la microrobotique ([Percipio Robotics](/blog/vivatech-2026-percipio-robotics-startups-bfc), spin-off de FEMTO-ST) ou les microsystèmes (SilMach).[^1] Pour une entreprise qui fabrique des composants à l’échelle du micromètre, pouvoir marcher jusqu’à la salle blanche ou discuter avec le chercheur à l’origine du procédé change concrètement la vitesse de développement."
    },
    {
      "type": "h2",
      "text": "Les dispositifs d’accompagnement, du local au national"
    },
    {
      "type": "p",
      "text": "Autour de ce parcours gravitent des acteurs publics qui financent, conseillent ou mettent en réseau. Sans prétendre à l’exhaustivité, voici les principaux."
    },
    {
      "type": "ul",
      "items": [
        "**Grand Besançon Métropole**, qui porte la technopole et accompagne l’implantation des entreprises sur le site.[^5]",
        "**La Région Bourgogne-Franche-Comté**, qui porte les aides régionales à l’innovation et à l’investissement des jeunes entreprises.",
        "**Bpifrance**, guichet national des aides à l’innovation (subventions, avances remboursables, prêts d’amorçage), pilote du plan Deeptech et opérateur du concours i-Lab, qui récompense chaque année des projets de création d’entreprises de technologies innovantes.[^6]",
        "**La French Tech Bourgogne-Franche-Comté**, labellisée Capitale French Tech, relais local des programmes de La French Tech et lieu de mise en réseau entre fondateurs.",
        "**Le Pôle des Microtechniques (PMT)**, pôle de compétitivité basé à Besançon, qui aide à monter des projets collaboratifs entre PME, grands groupes et laboratoires."
      ]
    },
    {
      "type": "p",
      "text": "Dans la pratique, une jeune pousse combine plusieurs de ces leviers : une aide Bpifrance pour la preuve de concept, un soutien régional pour l’équipement, un hébergement en pépinière, puis une levée de fonds lorsque le produit approche du marché. Le statut de jeune entreprise innovante et le crédit d’impôt recherche complètent souvent le montage."
    },
    {
      "type": "h2",
      "text": "TEMIS Santé, le versant médical"
    },
    {
      "type": "p",
      "text": "TEMIS ne se limite pas au plateau des microtechniques. Un second site, [TEMIS Santé](/blog/besancon-terrain-naturel-ia-sante-chu-isifc-temis), s’est développé à proximité du CHU Besançon Franche-Comté, autour de l’ingénierie biomédicale. On y trouve notamment le centre Bio Innovation, un bâtiment qui héberge startups et entreprises des dispositifs médicaux et des biothérapies, à proximité immédiate du CHU et de l’Établissement français du sang.[^1] La logique est la même que sur le site principal : rapprocher les startups des cliniciens qui testeront leurs dispositifs et des laboratoires qui les ont conçus. Les microtechniques y trouvent un débouché naturel, des instruments chirurgicaux miniaturisés aux capteurs implantables."
    },
    {
      "type": "callout",
      "tone": "tip",
      "title": "Pourquoi ça compte pour l’IA",
      "text": "L’intelligence artificielle bisontine ne se fera pas seulement dans le logiciel. Les entreprises de TEMIS produisent des capteurs, des microsystèmes et des instruments qui génèrent des données à traiter, souvent au plus près du matériel. Embarquer des modèles frugaux dans un composant, fiabiliser une mesure par apprentissage, automatiser le contrôle qualité en salle blanche : autant de sujets où les compétences en IA rencontrent le savoir-faire microtechnique. FEMTO-ST dispose d’ailleurs d’un département d’informatique qui travaille sur ces questions.[^2]"
    },
    {
      "type": "h2",
      "text": "Une force : la proximité"
    },
    {
      "type": "p",
      "text": "Ce qui distingue Besançon n’est pas la taille de son écosystème, modeste à l’échelle nationale, mais sa densité. Laboratoire, écoles, salle blanche, pépinière, financeurs et clients industriels se côtoient sur quelques hectares, et [le salon Micronora](/blog/micronora-2026-salon-microtechniques-acteurs-ia), qui se tient tous les deux ans dans la ville, offre une vitrine internationale à cette filière.[^1] Les défis restent ceux de toutes les deeptech de région : trouver des capitaux pour passer à l’échelle, recruter et retenir les talents, et faire connaître au-delà de la Franche-Comté des technologies souvent invisibles à l’œil nu."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "TEMIS – Technopole Microtechnique et Scientifique",
      "publisher": "TEMIS",
      "url": "https://www.temis.org/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 2,
      "title": "Institut FEMTO-ST",
      "publisher": "FEMTO-ST",
      "url": "https://www.femto-st.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 3,
      "title": "Université Marie et Louis Pasteur",
      "publisher": "Université Marie et Louis Pasteur",
      "url": "https://www.umlp.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 4,
      "title": "SUPMICROTECH-ENSMM, école d’ingénieurs de Besançon",
      "publisher": "SUPMICROTECH-ENSMM",
      "url": "https://www.supmicrotech.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 5,
      "title": "Grand Besançon Métropole",
      "publisher": "Grand Besançon Métropole",
      "url": "https://www.grandbesancon.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 6,
      "title": "Bpifrance – Espace presse",
      "publisher": "Bpifrance",
      "url": "https://presse.bpifrance.fr/",
      "type": "official",
      "date": "2026-09-06"
    }
  ]
};
